'use client';

/**
 * SectionHeader Component
 *
 * An uppercase, letter-spaced header for dashboard sections.
 * Provides visual hierarchy and clear section delineation.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { forwardRef } from 'react';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface SectionHeaderProps extends Omit<BoxProps, 'children'> {
  /**
   * Header text content
   */
  children: string;

  /**
   * Optional icon to display before text
   */
  icon?: React.ReactNode;

  /**
   * Optional badge/count to display after text
   */
  badge?: React.ReactNode;

  /**
   * Size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Color variant
   * @default 'muted'
   */
  color?: 'muted' | 'primary' | 'default';

  /**
   * Add bottom margin
   * @default true
   */
  gutterBottom?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledHeaderProps {
  $size: 'small' | 'medium' | 'large';
  $color: 'muted' | 'primary' | 'default';
  $gutterBottom: boolean;
}

const sizeConfig = {
  small: {
    fontSize: '0.6875rem',
    letterSpacing: '0.08em',
    iconSize: 14,
  },
  medium: {
    fontSize: '0.75rem',
    letterSpacing: '0.1em',
    iconSize: 16,
  },
  large: {
    fontSize: '0.85rem',
    letterSpacing: '0.08em',
    iconSize: 18,
  },
};

const StyledContainer = styled(
  Box,
  transientOptions,
)<StyledHeaderProps>(({ theme, $gutterBottom }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: $gutterBottom ? theme.spacing(2) : 0,
}));

const StyledText = styled(
  Typography,
  transientOptions,
)<{
  $size: 'small' | 'medium' | 'large';
  $color: 'muted' | 'primary' | 'default';
}>(({ theme, $size, $color }) => {
  const config = sizeConfig[$size];

  const colorMap = {
    muted: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.8)' : 'rgba(100, 116, 139, 0.9)',
    primary: theme.palette.primary.main,
    default: theme.palette.text.secondary,
  };

  return {
    fontSize: config.fontSize,
    fontWeight: 600,
    letterSpacing: config.letterSpacing,
    textTransform: 'uppercase',
    color: colorMap[$color],
    lineHeight: 1.5,
    userSelect: 'none',
  };
});

const IconWrapper = styled(
  Box,
  transientOptions,
)<{
  $size: 'small' | 'medium' | 'large';
  $color: 'muted' | 'primary' | 'default';
}>(({ theme, $size, $color }) => {
  const config = sizeConfig[$size];

  const colorMap = {
    muted: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.6)' : 'rgba(100, 116, 139, 0.7)',
    primary: theme.palette.primary.main,
    default: theme.palette.text.secondary,
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colorMap[$color],
    '& svg': {
      fontSize: config.iconSize,
      width: config.iconSize,
      height: config.iconSize,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(
  ({ children, icon, badge, size = 'medium', color = 'muted', gutterBottom = true, ...props }, ref) => {
    return (
      <StyledContainer ref={ref} $size={size} $color={color} $gutterBottom={gutterBottom} {...props}>
        {icon && (
          <IconWrapper $size={size} $color={color}>
            {icon}
          </IconWrapper>
        )}
        <StyledText $size={size} $color={color}>
          {children}
        </StyledText>
        {badge}
      </StyledContainer>
    );
  },
);

SectionHeader.displayName = 'SectionHeader';

export default SectionHeader;
